import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Grid,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
} from '@mui/material'
import {
  Group as GroupIcon,
  Person as PersonIcon,
} from '@mui/icons-material'
import { useForm, Controller } from 'react-hook-form'
import { useAppSelector, useAppDispatch } from '@hooks/redux'
import {
  updateGroup,
  deleteGroup,
  fetchGroup,
  createGroup,
  setSelectedGroup,
  inviteUserToGroup,
  removeUserFromGroup
} from '@store/slices/groupsSlice'
import { Group } from '@types'
import ColorPicker from '@components/common/ColorPicker'
import SaveToolbar from '@components/common/SaveToolbar'

interface GroupFormData {
  name: string
  description: string
  hex_color: string
  type: string
  public: boolean
}

const GroupEdit: React.FC = () => {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const dispatch = useAppDispatch()

  const { selectedGroup, loadingState, error } = useAppSelector((state) => state.groups)

  const [showLoadingIcon, setShowLoadingIcon] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [inviteEmail, setInviteEmail] = useState('')

  const group = selectedGroup

  const { control, handleSubmit, setValue, watch, formState: { errors, isValid } } = useForm<GroupFormData>({
    defaultValues: {
      name: '',
      description: '',
      hex_color: '#F8B133',
      type: 'collaboration',
      public: false,
    },
  })

  const groupTypes = [
    { value: 'collaboration', label: 'Collaboration Group' },
    { value: 'research', label: 'Research Group' },
    { value: 'education', label: 'Education Group' },
    { value: 'commercial', label: 'Commercial Group' },
  ]

  useEffect(() => {
    if (id) {
      dispatch(fetchGroup(parseInt(id)))
    }
  }, [dispatch, id])

  useEffect(() => {
    if (group && id) {
      setValue('name', group.name)
      setValue('description', group.description || '')
      setValue('hex_color', group.hex_color || '#F8B133')
      setValue('type', group.type || 'collaboration')
      setValue('public', group.is_public ?? group.public ?? false)
      dispatch(setSelectedGroup(group))
    } else if (!id) {
      // Reset form for creation
      setValue('name', '')
      setValue('description', '')
      setValue('hex_color', '#F8B133')
      setValue('type', 'collaboration')
      setValue('public', false)
      dispatch(setSelectedGroup(null))
    }
  }, [group, id, setValue, dispatch])

  const getTitle = () => {
    if (!id) return 'Create New Group'
    return group ? `Edit Group: ${group.name}` : 'Edit Group'
  }

  const isGroupEditable = () => {
    if (!id) return true
    return group ? (group.owner || group.admin) : false
  }

  const handleColorChange = (color: string) => {
    setValue('hex_color', color)
  }

  const handleSaveGroup = async (data: GroupFormData) => {
    if (id && !group) return

    setShowLoadingIcon(true)
    setErrorMessage('')

    try {
      const groupData: Partial<Group> = {
        name: data.name,
        description: data.description,
        hex_color: data.hex_color,
        type: data.type,
        is_public: data.public,
      }

      if (id && group) {
        await dispatch(updateGroup({ id: group.id, data: groupData }))
      } else {
        await dispatch(createGroup(groupData))
      }
      navigate('/groups')
    } catch (err: any) {
      setErrorMessage(err.message || 'Failed to update group')
    } finally {
      setShowLoadingIcon(false)
    }
  }

  const handleDeleteGroup = async () => {
    if (!group) return

    if (window.confirm(`Are you sure you want to delete group "${group.name}"? This action cannot be undone.`)) {
      setShowLoadingIcon(true)
      try {
        await dispatch(deleteGroup(group.id))
        navigate('/groups')
      } catch (err: any) {
        setErrorMessage(err.message || 'Failed to delete group')
      } finally {
        setShowLoadingIcon(false)
      }
    }
  }

  const handleInviteUser = async () => {
    if (!group || !inviteEmail.trim()) return

    setShowLoadingIcon(true)
    try {
      await dispatch(inviteUserToGroup({ groupId: group.id, email: inviteEmail }))
      setInviteEmail('')
    } catch (err: any) {
      setErrorMessage(err.message || 'Failed to invite user')
    } finally {
      setShowLoadingIcon(false)
    }
  }

  // `membershipId` comes from Group.members[].id, which is a GroupMembership id.
  const handleRemoveUser = async (membershipId: number) => {
    if (!group) return

    if (window.confirm('Are you sure you want to remove this user from the group?')) {
      setShowLoadingIcon(true)
      try {
        await dispatch(removeUserFromGroup({ groupId: group.id, membershipId }))
      } catch (err: any) {
        setErrorMessage(err.message || 'Failed to remove user')
      } finally {
        setShowLoadingIcon(false)
      }
    }
  }

  if (id && !group) {
    return (
      <Container sx={{ py: 3 }}>
        <Typography variant="h4" color="error" align="center">
          Group not found
        </Typography>
      </Container>
    )
  }

  if (!isGroupEditable()) {
    return (
      <Container sx={{ py: 3 }}>
        <Typography variant="h4" color="error" align="center">
          Sorry, group "{group?.name}" is not editable
        </Typography>
      </Container>
    )
  }

  if (loadingState === 'pending') {
    return (
      <Container>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={50} />
        </Box>
      </Container>
    )
  }

  return (
    <Container maxWidth="lg" sx={{ py: 2, px: { xs: 2, sm: 3 } }}>
      <SaveToolbar
        onSave={handleSubmit(handleSaveGroup)}
        onDelete={group?.owner ? handleDeleteGroup : undefined}
        saveDisabled={!isValid || showLoadingIcon}
        loading={showLoadingIcon}
        showDelete={!!group?.owner}
      />

      <Typography variant="h4" component="h1" gutterBottom sx={{ mt: 2 }}>
        {getTitle()}
      </Typography>

        {errorMessage && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setErrorMessage('')}>
            {errorMessage}
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit(handleSaveGroup)}>
          <Grid container spacing={3}>
            {/* Group Settings */}
            <Grid item xs={12} md={8}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Group Settings
                  </Typography>

                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <Controller
                        name="name"
                        control={control}
                        rules={{ required: 'Group name is required' }}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="Group Name"
                            error={!!errors.name}
                            helperText={errors.name?.message}
                            placeholder="Enter group name"
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <InputLabel>Group Type</InputLabel>
                        <Controller
                          name="type"
                          control={control}
                          render={({ field }) => (
                            <Select {...field} label="Group Type">
                              {groupTypes.map((type) => (
                                <MenuItem key={type.value} value={type.value}>
                                  {type.label}
                                </MenuItem>
                              ))}
                            </Select>
                          )}
                        />
                      </FormControl>
                    </Grid>

                    <Grid item xs={12}>
                      <Controller
                        name="description"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="Description"
                            multiline
                            rows={3}
                            placeholder="Describe the purpose of this group..."
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle1" gutterBottom>
                        Group Color
                      </Typography>
                      <Controller
                        name="hex_color"
                        control={control}
                        render={({ field }) => (
                          <ColorPicker
                            color={field.value}
                            onChange={handleColorChange}
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle1" gutterBottom>
                        Preview
                      </Typography>
                      <Card
                        sx={{
                          border: `2px solid ${watch('hex_color')}`,
                          p: 2,
                          textAlign: 'center',
                        }}
                      >
                        <GroupIcon sx={{ fontSize: 48, color: watch('hex_color'), mb: 1 }} />
                        <Typography variant="h6">
                          {watch('name') || 'Your Group Name'}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {groupTypes.find(t => t.value === watch('type'))?.label}
                        </Typography>
                        <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 1 }}>
                          {group?.members?.length || 0} members
                        </Typography>
                      </Card>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Group Members - Only show in edit mode */}
            {id && (
              <Grid item xs={12} md={4}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Group Members
                    </Typography>

                    {/* Invite New Member */}
                    <Box sx={{ mb: 3 }}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Invite by Email"
                        value={inviteEmail}
                        onChange={(e) => setInviteEmail(e.target.value)}
                        placeholder="user@example.com"
                        sx={{ mb: 1 }}
                      />
                      <Button
                        variant="outlined"
                        size="small"
                        fullWidth
                        onClick={handleInviteUser}
                        disabled={!inviteEmail.trim() || showLoadingIcon}
                      >
                        Send Invitation
                      </Button>
                    </Box>

                    {/* Current Members */}
                    <Box>
                      <Typography variant="subtitle2" gutterBottom>
                        Current Members ({group?.members?.length || 0})
                      </Typography>

                      {group?.members?.map((member) => (
                        <Box
                          key={member.id}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            py: 1,
                            borderBottom: 1,
                            borderColor: 'divider',
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <PersonIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                            <Box>
                              <Typography variant="body2">
                                {member.name}
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                {member.email}
                              </Typography>
                            </Box>
                          </Box>

                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {member.role && (
                              <Chip
                                label={member.role}
                                size="small"
                                color={member.role === 'owner' ? 'primary' : 'default'}
                                variant="outlined"
                              />
                            )}

                            {group?.owner && member.id !== group?.owner_id && (
                              <Button
                                size="small"
                                color="error"
                                onClick={() => handleRemoveUser(member.id)}
                              >
                                Remove
                              </Button>
                            )}
                          </Box>
                        </Box>
                      ))}

                      {(!group?.members || group?.members.length === 0) && (
                        <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center', py: 2 }}>
                          No members yet. Invite users to collaborate.
                        </Typography>
                      )}
                    </Box>
                  </CardContent>
                </Card>

                {/* Group Statistics */}
                <Card sx={{ mt: 2 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Group Statistics
                    </Typography>

                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="h4" color="primary">
                            {group?.hives?.length || 0}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            Shared Hives
                          </Typography>
                        </Box>
                      </Grid>

                      <Grid item xs={6}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="h4" color="success.main">
                            {group?.members?.length || 0}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            Members
                          </Typography>
                        </Box>
                      </Grid>

                      <Grid item xs={6}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="h4" color="info.main">
                            {group?.created_at ? new Date(group.created_at).getFullYear() : 'N/A'}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            Created
                          </Typography>
                        </Box>
                      </Grid>

                      <Grid item xs={6}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="h4" color="warning.main">
                            {group?.public ? 'Public' : 'Private'}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            Visibility
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            )}
          </Grid>
        </form>
    </Container>
  )
}

export default GroupEdit
